/**
 * PROP-1250: disabled on prod to suppress multi-locale routing, language switcher,
 * hreflang tags and locale variants in sitemaps.
 * On non-prod environments translations remain enabled for testing.
 */
export const TRANSLATIONS_ENABLED = process.env.BUILD_TYPE !== 'prod';

/**
 * Supported locales for Phase 1 i18n (no URL change).
 * Must match existing message files in messages/*.json
 */
export const supportedLocales = [
  'de',
  'en',
  'es',
  'hu',
  'sl',
  'hr',
  'hbs',
  'cs',
  'sk',
  'rom',
  'ro',
  'tr',
  'pl',
  'sq',
  'it',
  'fr',
  'ru',
] as const;

export type Locale = (typeof supportedLocales)[number];

export const defaultLocale: Locale = 'de';

/** Full names for dropdown list */
export const localeNames: Record<Locale, string> = {
  de: 'Deutsch',
  en: 'English',
  es: 'Español',
  hu: 'Magyar',
  sl: 'Slovenščina',
  hr: 'Hrvatski',
  hbs: 'Srpski/Hrvatski/Bosanski',
  cs: 'Čeština',
  sk: 'Slovenčina',
  rom: 'Romani',
  ro: 'Română',
  tr: 'Türkçe',
  pl: 'Polski',
  sq: 'Shqip',
  it: 'Italiano',
  fr: 'Français',
  ru: 'Русский',
};

/** Short codes for header (e.g. DE, EN) */
export const localeShortCodes: Record<Locale, string> = {
  de: 'DE',
  en: 'EN',
  es: 'ES',
  hu: 'HU',
  sl: 'SL',
  hr: 'HR',
  hbs: 'HBS',
  cs: 'CS',
  sk: 'SK',
  rom: 'ROM',
  ro: 'RO',
  tr: 'TR',
  pl: 'PL',
  sq: 'SQ',
  it: 'IT',
  fr: 'FR',
  ru: 'RU',
};

export function isSupportedLocale(value: string): value is Locale {
  return supportedLocales.includes(value as Locale);
}

/** BCP 47 for Accept-Language header */
export const localeToAcceptLanguage: Record<Locale, string> = {
  de: 'de-DE',
  en: 'en-US',
  es: 'es-ES',
  hu: 'hu-HU',
  sl: 'sl-SI',
  hr: 'hr-HR',
  hbs: 'hbs-BA',
  cs: 'cs-CZ',
  sk: 'sk-SK',
  rom: 'rom-RO',
  ro: 'ro-RO',
  tr: 'tr-TR',
  pl: 'pl-PL',
  sq: 'sq-AL',
  it: 'it-IT',
  fr: 'fr-FR',
  ru: 'ru-RU',
};

export function getAcceptLanguageHeader(locale: Locale): string {
  return localeToAcceptLanguage[locale] ?? locale;
}

/**
 * Parse Accept-Language header and return the first matching supported locale.
 * Used to suggest a locale for the language banner (no cookie/pathname).
 */
export function getSuggestedLocaleFromAcceptLanguage(acceptLanguage: string | null): Locale | null {
  if (!acceptLanguage || typeof acceptLanguage !== 'string') return null;
  const set = new Set(supportedLocales);
  const parts = acceptLanguage.split(',').map((p) => p.trim());
  for (const part of parts) {
    const code = part.split(/[-;]/)[0]?.toLowerCase();
    if (code && set.has(code as Locale)) return code as Locale;
  }
  return null;
}
